import cx from "classnames";
import styles from "./Newsletters.module.scss";
import SectionHeading from "./SectionHeading";
import { useId } from "@reach/auto-id";
import { useMutation } from "urql";
import { gql } from "utils/urql";
import { Form, Field } from "react-final-form";
import {
  AnonymousNewsletterSignupMutationVariables,
  AnonymousNewsletterSignupMutation,
} from "types/api";

import Checkmark from "assets/checkmark-circle.svg";
import VisuallyHidden from "components/VisuallyHidden";
import { getDataLayer } from "utils/analytics";

type AlertProps = React.HTMLProps<HTMLDivElement> & {
  type: "assertive" | "polite";
  children: React.ReactNode;
};

interface FormValues {
  email: string;
}

const NEWSLETTER_MUTATION = gql`
  mutation anonymousNewsletterSignup($input: AnonymousNewsletterSignup!) {
    anonymousNewsletterSignup(input: $input) {
      existingUser
    }
  }
`;

const Alert = ({ type, children, ...props }: AlertProps) => {
  return (
    <div
      {...props}
      role={type === "assertive" ? "alert" : "status"}
      aria-live={type}
    >
      {children}
    </div>
  );
};

const SuccessMessage = ({
  existingUser,
}: {
  existingUser?: boolean | null;
}) => {
  return (
    <div className={cx(styles.success)}>
      <Checkmark aria-hidden={true} className={styles.checkmark} />
      <p className={styles.thanks}>Thanks for signing up!</p>
      <p className={styles.confirmation}>
        {existingUser
          ? "Your newsletter subscription preferences have been updated."
          : "Please check your email to confirm your subscription."}
      </p>
      <a
        href="https://www.theatlantic.com/newsletters/sign-up/"
        className={styles.seeMore}
      >
        See more newsletters
      </a>
    </div>
  );
};

const ATLANTIC_DAILY_ID = "7581216";

const Newsletters = () => {
  const [signupResult, submitSignup] = useMutation<
    AnonymousNewsletterSignupMutation,
    AnonymousNewsletterSignupMutationVariables
  >(NEWSLETTER_MUTATION);

  const emailInputId = useId();
  const errorId = useId();
  const titleId = useId();
  const descriptionId = useId();

  const onSubmit = async ({ email }: FormValues) => {
    const result = await submitSignup({
      input: {
        email,
        ids: [ATLANTIC_DAILY_ID],
      },
    });

    const graphqlError =
      result.error &&
      result.error?.graphQLErrors.map((err) => err.message).join(" ");

    const networkError = result.error && result.error.networkError;

    if (graphqlError) {
      return {
        email: graphqlError,
      };
    }

    if (networkError) {
      return {
        FORM_ERROR: "An unknown error occurred.",
      };
    }

    getDataLayer().push({
      event: "Newsletter Signup",
      user: {
        email,
        newsletter: {
          id: ATLANTIC_DAILY_ID,
          name: "The Atlantic Daily",
        },
      },
    });
  };

  const existingUser =
    signupResult.data?.anonymousNewsletterSignup?.existingUser;

  return (
    <section data-testid="homepage-newsletters">
      <SectionHeading
        title="Newsletters"
        secondaryLinkText="See All"
        url="https://www.theatlantic.com/newsletters/"
        analyticsSlug="newsletters"
      />
      <div className={styles.content}>
        <p className={styles.label}>The Atlantic Daily</p>
        <p className={styles.description}>
          Get our editors’ guide to what matters in the world, delivered to your
          inbox every weekday.
        </p>
        <Form onSubmit={onSubmit}>
          {({
            handleSubmit,
            submitting,
            invalid,
            submitSucceeded,
            submitErrors,
          }) => {
            return (
              <>
                <form
                  className={styles.form}
                  onSubmit={handleSubmit}
                  aria-labelledby={titleId}
                  aria-describedby={descriptionId}
                  aria-hidden={submitSucceeded}
                >
                  <Field name="email" type="email">
                    {({ input, meta }) => {
                      return (
                        <>
                          <VisuallyHidden>
                            <label htmlFor={emailInputId}>Email Address</label>
                          </VisuallyHidden>

                          <input
                            {...input}
                            id={emailInputId}
                            required={true}
                            aria-describedby={errorId}
                            placeholder="Enter your email"
                            className={cx(
                              styles.input,
                              invalid && styles.inputError
                            )}
                          />
                          <div className={styles.buttonContainer}>
                            <button
                              type="submit"
                              disabled={submitting || submitSucceeded}
                              className={
                                submitSucceeded
                                  ? styles.invisible
                                  : styles.button
                              }
                              data-action="newsletter signup - newsletters"
                              data-label={ATLANTIC_DAILY_ID}
                            >
                              <span
                                aria-hidden={submitting}
                                className={cx(submitting && styles.invisible)}
                              >
                                Submit
                              </span>
                              {submitting && (
                                <span
                                  aria-label="Loading"
                                  className={styles.buttonLoading}
                                />
                              )}
                            </button>
                          </div>

                          <Alert
                            type="assertive"
                            id={errorId}
                            className={styles.error}
                          >
                            {invalid && meta.touched && meta.submitError}
                            {invalid &&
                              meta.touched &&
                              submitErrors &&
                              submitErrors.FORM_ERROR}
                          </Alert>
                        </>
                      );
                    }}
                  </Field>
                </form>

                <Alert type="polite">
                  {submitSucceeded && (
                    <SuccessMessage existingUser={existingUser} />
                  )}
                </Alert>
              </>
            );
          }}
        </Form>
      </div>
    </section>
  );
};

export default Newsletters;
